import { useState } from "react"
import { Phone, MessageSquare, ExternalLink, ClipboardList, X, Copy, Check } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { darfAufgabeAnlegen } from "@/lib/aufgabeVerknuepfung"

export type PrepareResult = {
  kind: "email" | "call" | "hero" | "termin"
  label: string
  reason: string
  todo: string | null
  step_name: string | null
  hero_url: string
  email_id: number | null
  mode: "reply" | "new"
  draft_kept: boolean
  anchor_found: boolean
  anchor_subject: string | null
  anchor_date: string | null
  slots: string[]
  phone: string | null
  sms_text: string | null
  sms_length: number
  task_prefill: Record<string, string>
}

/**
 * Modal fuer die To-dos, die das Portal NICHT selbst erledigen kann.
 *
 * kind=call — Telefon-Leads ohne Mailadresse (auf Prod die Mehrheit der
 *   "Kontakt aufnehmen"-Alerts). Nummer zum Anwaehlen + fertige SMS-Vorlage
 *   zum Kopieren. Bewusst KEIN Sende-Knopf: eine SMS an einen Kunden ist eine
 *   Aussenwirkung, die nicht an einem einzelnen Klick haengen soll.
 * kind=hero — Angebot/Rechnung erstellen. Deeplink ins Projekt, und weil das
 *   Portal die Arbeit nicht abnehmen kann: Aufgabe mit Frist anlegen.
 */
export function TodoActionModal({
  open, result, contactName, onClose,
}: {
  open: boolean
  result: PrepareResult | null
  contactName: string
  onClose: () => void
}) {
  const [copied, setCopied] = useState<string | null>(null)
  const [creatingTask, setCreatingTask] = useState(false)
  const [taskDone, setTaskDone] = useState(false)

  if (!open || !result) return null

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 1800)
    } catch {
      toast("Kopieren nicht moeglich — Text bitte markieren", "error")
    }
  }

  const createTask = async () => {
    // #149 (+ #22) — `task_prefill` liefert die Verknuepfung als String und
    // laesst sie leer, wenn der Vorschlag keine hergab. Einen Empfaenger
    // bringt er nicht mit: dann geht die Aufgabe an niemanden.
    if (!(await darfAufgabeAnlegen({
      mitarbeiter: result.task_prefill?.target_user,
      contactId: result.task_prefill?.contact_id,
      projectId: result.task_prefill?.project_match_id,
    }))) return
    setCreatingTask(true)
    try {
      const r = await fetch("/api/v1/tasks", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(result.task_prefill),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json().catch(() => ({}))
      if (data?.error) throw new Error(String(data.error))
      setTaskDone(true)
      toast("✓ Aufgabe in HERO angelegt")
    } catch (e) {
      toast("Aufgabe fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setCreatingTask(false)
    }
  }

  const isCall = result.kind === "call"
  const isTermin = result.kind === "termin"

  return (
    <div
      className="pp-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="pp-card"
        style={{ width: 520, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto", padding: 0 }}
      >
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "14px 16px",
          borderBottom: "0.5px solid rgba(44,44,42,0.1)",
        }}>
          {isCall
            ? <Phone size={16} color="#3C3489" />
            : <ExternalLink size={16} color="#3C3489" />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#2C2C2A" }}>
              {result.todo || result.label}
            </div>
            <div style={{ fontSize: 12, color: "#888780", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {contactName}
            </div>
          </div>
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm" style={{ padding: "4px 6px" }}>
            <X size={13} />
          </button>
        </div>

        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontSize: 12.5, color: "#5F5E5A" }}>{result.reason}</div>

          {isCall && result.phone && (
            <div>
              <div style={{ fontSize: 12, color: "#888780", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Anrufen
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <a
                  href={`tel:${result.phone}`}
                  className="pp-btn pp-btn-sm pp-btn-primary"
                  style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <Phone size={12} /> {result.phone}
                </a>
                <button type="button" className="pp-btn pp-btn-sm" onClick={() => copy(result.phone || "", "tel")}>
                  {copied === "tel" ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            </div>
          )}

          {isCall && result.sms_text && (
            <div>
              <div style={{
                fontSize: 12, color: "#888780", textTransform: "uppercase", letterSpacing: 0.5,
                marginBottom: 6, display: "flex", alignItems: "center", gap: 6,
              }}>
                <MessageSquare size={11} /> SMS-Vorlage
                <span style={{ textTransform: "none", letterSpacing: 0, color: result.sms_length > 160 ? "#9A3412" : "#888780" }}>
                  {result.sms_length} Zeichen{result.sms_length > 160 ? " — 2 Segmente" : ""}
                </span>
              </div>
              <div style={{
                background: "#F5F4F0", border: "0.5px solid rgba(44,44,42,0.1)", borderRadius: 6,
                padding: 10, fontSize: 12.5, color: "#2C2C2A", whiteSpace: "pre-wrap",
              }}>{result.sms_text}</div>
              <button
                type="button"
                className="pp-btn pp-btn-sm"
                style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6 }}
                onClick={() => copy(result.sms_text || "", "sms")}
              >
                {copied === "sms" ? <><Check size={12} /> kopiert</> : <><Copy size={12} /> Text kopieren</>}
              </button>
              <div style={{ fontSize: 12, color: "#888780", marginTop: 6 }}>
                Versand bewusst nicht per Klick — Text kopieren und im SMS-Tool senden.
              </div>
            </div>
          )}

          {!isCall && (
            <div>
              <div style={{ fontSize: 12, color: "#888780", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                {isTermin ? "Termin steht" : "In HERO erledigen"}
              </div>
              <a
                href={result.hero_url}
                target="_blank" rel="noreferrer"
                className="pp-btn pp-btn-sm pp-btn-primary"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <ExternalLink size={12} /> Projekt in HERO öffnen
              </a>
            </div>
          )}

          <div style={{ borderTop: "0.5px solid rgba(44,44,42,0.1)", paddingTop: 12 }}>
            <div style={{ fontSize: 12, color: "#888780", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
              Nicht vergessen
            </div>
            <div style={{ fontSize: 12.5, color: "#5F5E5A", marginBottom: 8 }}>
              Aufgabe in HERO anlegen, Frist {fmtDue(result.task_prefill?.due_date)}:
              <div style={{ color: "#2C2C2A", marginTop: 3 }}>„{result.task_prefill?.title}“</div>
            </div>
            <button
              type="button"
              className="pp-btn pp-btn-sm"
              disabled={creatingTask || taskDone}
              onClick={createTask}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {taskDone
                ? <><Check size={12} /> angelegt</>
                : <><ClipboardList size={12} /> {creatingTask ? "Lege an…" : "Aufgabe anlegen"}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function fmtDue(iso?: string): string {
  if (!iso) return "—"
  const [y, m, d] = iso.split("-")
  return d && m && y ? `${d}.${m}.${y}` : iso
}
